import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, PanInfo, useDragControls } from 'framer-motion';
import { Button, IconButton, Avatar, Divider } from '@mui/material';
import { Person, Email, Phone, LocationOn, Edit, Settings, Logout, Star, ShoppingBag } from '@mui/icons-material';
import { colors, borderRadius, spacing, typography, shadows } from '../styles/theme';

// Тестовые данные профиля
const mockProfile = {
  id: '1',
  name: 'Алексей Иванов',
  email: 'alexey.ivanov@example.com',
  phone: '+7 (999) 123-45-67',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
  address: 'Москва, ул. Тверская, д. 15',
  joinDate: '15 марта 2023',
  totalOrders: 47,
  favoriteItems: 12,
  rating: 4.8,
  level: 'VIP',
};

const DragHandle = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing.sm};
  cursor: grab;
  touch-action: none;
  user-select: none;
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover::after {
    background: ${colors.primary};
    transform: scaleY(1.2);
  }

  &:active::after {
    background: ${colors.primary};
    transform: scaleY(1.5);
  }

  &::after {
    content: '';
    width: 40px;
    height: 4px;
    background: ${colors.border};
    border-radius: 2px;
    transition: all 0.2s ease;
  }
`;

const Content = styled.div`
  padding: ${spacing.lg};
  max-height: calc(90vh - 60px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  /* Скрываем scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: ${spacing.xl};
`;

const ProfileAvatar = styled(Avatar)`
  width: 80px;
  height: 80px;
  margin-bottom: ${spacing.md};
  border: 3px solid ${colors.primary};
  box-shadow: ${shadows.md};
`;

const ProfileName = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xxl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.textPrimary};
  margin: 0 0 ${spacing.xs} 0;
`;

const ProfileLevel = styled.div`
  background: linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark});
  color: ${colors.textInverse};
  padding: ${spacing.xs} ${spacing.md};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  margin-bottom: ${spacing.md};
`;

const StatsContainer = styled.div`
  display: flex;
  gap: ${spacing.md};
  margin-bottom: ${spacing.xl};
`;

const StatItem = styled.div`
  flex: 1;
  background: ${colors.backgroundTertiary};
  padding: ${spacing.md};
  border-radius: ${borderRadius.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xxxl};
  font-weight: ${typography.fontWeight.extraBold};
  color: ${colors.primary};
  margin-bottom: ${spacing.xs};
`;

const StatLabel = styled.div`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
  font-weight: ${typography.fontWeight.medium};
`;

const InfoSection = styled.div`
  margin-bottom: ${spacing.xl};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md} 0;
  border-bottom: 1px solid ${colors.borderLight};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${colors.backgroundTertiary};
  border-radius: ${borderRadius.md};
  color: ${colors.primary};
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${spacing.xs};
`;

const InfoValue = styled.div`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  color: ${colors.textPrimary};
  font-weight: ${typography.fontWeight.medium};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing.md};
  margin-top: ${spacing.xl};
`;

const ActionButton = styled(Button)`
  flex: 1;
  padding: ${spacing.md};
  border-radius: ${borderRadius.lg};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.semibold};
  text-transform: none;
  transition: all 0.2s ease;

  &.MuiButton-outlined {
    border-color: ${colors.border};
    color: ${colors.textPrimary};

    &:hover {
      border-color: ${colors.primary};
      background: rgba(0, 122, 255, 0.05);
    }
  }

  &.MuiButton-contained {
    background: ${colors.primary};
    color: ${colors.textInverse};

    &:hover {
      background: ${colors.primaryDark};
      transform: translateY(-1px);
      box-shadow: ${shadows.md};
    }
  }
`;

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ isOpen, onClose }) => {
  const dragControls = useDragControls();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Блокируем скролл body когда шторка открыта
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    const { offset, velocity } = info;
    if (offset.y > 100 || (offset.y > 50 && velocity.y > 500)) {
      onClose();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-end',
          touchAction: 'none',
        }}
        onClick={handleBackgroundClick}
      >
        <motion.div
          ref={drawerRef}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          drag="y"
          dragControls={dragControls}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
          style={{
            background: colors.backgroundSecondary,
            borderRadius: `${borderRadius.xl} ${borderRadius.xl} 0 0`,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            touchAction: 'none',
          }}
        >
          <DragHandle
            onPointerDown={(e) => {
              e.preventDefault();
              dragControls.start(e);
            }}
          />

          <Content>
            <ProfileHeader>
              <ProfileAvatar src={mockProfile.avatar} alt={mockProfile.name}>
                <Person sx={{ fontSize: 40 }} />
              </ProfileAvatar>
              <ProfileName>{mockProfile.name}</ProfileName>
            </ProfileHeader>

            <StatsContainer>
              <StatItem>
                <StatValue>{mockProfile.totalOrders}</StatValue>
                <StatLabel>Заказов</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>{mockProfile.favoriteItems}</StatValue>
                <StatLabel>Избранных</StatLabel>
              </StatItem>
            </StatsContainer>

            <InfoSection>
              <InfoItem>
                <InfoIcon>
                  <Email />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>{mockProfile.email}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <Phone />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Телефон</InfoLabel>
                  <InfoValue>{mockProfile.phone}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <LocationOn />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Адрес</InfoLabel>
                  <InfoValue>{mockProfile.address}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>
                  <Star />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Дата регистрации</InfoLabel>
                  <InfoValue>{mockProfile.joinDate}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoSection>

            <ActionButtons>
              <ActionButton variant="outlined" startIcon={<Edit />}>
                Редактировать профиль
              </ActionButton>
              <ActionButton variant="outlined" startIcon={<Settings />}>
                Настройки
              </ActionButton>
            </ActionButtons>

            <ActionButton
              variant="contained"
              startIcon={<Logout />}
              sx={{
                marginTop: spacing.lg,
                background: '#FF3B30',
                '&:hover': {
                  background: '#E53935',
                }
              }}
            >
              Выйти
            </ActionButton>
          </Content>
        </motion.div>
      </motion.div>
    </>
  );
};

export default ProfileDrawer;
